// externals
import { RSAA } from 'redux-api-middleware';
import { v4 as generateUuid } from 'uuid';
import type { Dispatch } from 'redux';
import { COX_AUTO_CORRELATION_ID_HEADER } from '@makemydeal/dr-shared-bff-types';

// libraries
import type { IConfigObj } from '@makemydeal/dr-common-utils';
import { buildFromConfig } from '@makemydeal/dr-common-utils';
import type { RSAACallAction, RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import type { FilterObjectForSemanticSearch } from '@makemydeal/dr-platform-types';

// consts/enums
import {
    ADD_NEW_SCENARIO_WITH_VEHICLE,
    RESET_ON_ADD_NEW_SCENARIO,
    CHANGE_VEHICLE_CANCEL,
    CHANGE_VEHICLE_CLICKED,
    FETCH_CVI_FAILURE,
    FETCH_CVI_REQUEST,
    FETCH_CVI_SUCCESS,
    NEW_VEHICLE_SELECTED,
    PROCESS_CVI_VEHICLES,
    PROCESS_SWITCH_VEHICLE,
    SEARCH_VEHICLE_FAILURE,
    SEARCH_VEHICLE_REQUEST,
    SEARCH_VEHICLE_SUCCESS,
    SEARCH_VEHICLES_CLICKED,
    VEHICLE_CHANGE_FAILURE,
    VEHICLE_CHANGE_SUCCESS,
    FETCH_CATALOG_YEARS_FAILURE,
    FETCH_CATALOG_YEARS_REQUEST,
    FETCH_CATALOG_YEARS_SUCCESS,
    FETCH_CATALOG_MAKES_FAILURE,
    FETCH_CATALOG_MAKES_REQUEST,
    FETCH_CATALOG_MAKES_SUCCESS,
    FETCH_CATALOG_MODELS_REQUEST,
    FETCH_CATALOG_MODELS_SUCCESS,
    FETCH_CATALOG_MODELS_FAILURE,
    FETCH_CATALOG_TRIMS_REQUEST,
    FETCH_CATALOG_TRIMS_SUCCESS,
    FETCH_CATALOG_TRIMS_FAILURE,
    FETCH_CATALOG_VEHICLES_REQUEST,
    FETCH_CATALOG_VEHICLES_SUCCESS,
    FETCH_CATALOG_VEHICLES_FAILURE,
    CLEAR_CATALOG_VEHICLES,
    UPDATE_CUSTOM_VEHICLE,
    CHANGE_VEHICLE_TAB_CHANGED,
    UPDATE_VEHICLE_CUSTOM_DATA,
    SET_SEARCH_QUERY,
    SEARCH_STOCK_NUMBER_REQUEST,
    SEARCH_STOCK_NUMBER_SUCCESS,
    SEARCH_STOCK_NUMBER_FAILURE,
    VEHICLE_CHANGE_FINISH,
    VEHICLE_AGENT_REQUEST,
    VEHICLE_AGENT_SUCCESS,
    VEHICLE_AGENT_FAILURE,
    SET_AGENT_PROMPT_ATTR,
    PROCESS_VEHICLE_AGENT_REQUEST,
    DISMISS_INVENTORY_FALLBACK,
    VEHICLE_AGENT_FILTER_REQUEST,
    VEHICLE_AGENT_FILTER_SUCCESS,
    VEHICLE_AGENT_FILTER_FAILURE,
    SET_OPEN_INCENTIVES_AFTER_CHANGE,
    SET_APPLY_VEHICLE_REQUESTED,
    SET_ADD_NEW_SCENARIO_REQUESTED,
    AI_SEARCH_VIN_INVENTORY_REQUEST,
    AI_SEARCH_VIN_INVENTORY_SUCCESS,
    AI_SEARCH_VIN_INVENTORY_FAILURE
} from './actions';

// types
import type { IVehicle } from '@makemydeal/dr-platform-types';
import type { VehicleObjectForSimilaritySearch } from '@makemydeal/dr-platform-types';
import type {
    AppliedFilter,
    ChangeVehicleCustomVehicle,
    RetentionDecisions,
    StateTree,
    VehicleCustomData,
    VehicleSearchOptions,
    VehicleSearchV2QueryParams
} from '../types';

// utils
import { mapAppliedFiltersToQueryParams } from '../utils/appliedFilterTags';
import { proxyFetch } from '../utils/proxyFetch';
import { withNewRelic } from '../decorators/NewRelic';
import type { AI_REQUEST_TRIGGER } from '../constants';
import { AI_PROMPT_MODE, AI_PROMPT_PLACEHOLDER } from '../constants';

export const fetchCviVehiclesResults = (
    query: string,
    orgId: string,
    bffEndpointConfig: IConfigObj
): ((dispatch: Dispatch<any, any>) => void) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, 'commonOrg');
    const endpoint = `${proxyUrl}/${orgId}/vehicles${query ? `?vehicleIdentifier=${query}` : ''}`;

    return (dispatch) => {
        dispatch({
            [RSAA]: {
                endpoint,
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                types: [FETCH_CVI_REQUEST, FETCH_CVI_SUCCESS, FETCH_CVI_FAILURE],
                fetch: proxyFetch
            } satisfies RSAACallRequest
        } as RSAACallAction);
        dispatch({ type: SET_SEARCH_QUERY, payload: { searchQuery: query } });
    };
};

const buildVehicleSearchV2Url = (params: VehicleSearchV2QueryParams, bffEndpointConfig: IConfigObj) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig);
    const filteredParams = Object.fromEntries(Object.entries(params).filter(([_, value]) => !!value));
    const searchParams = new URLSearchParams(filteredParams);
    const queryString = searchParams.toString();

    return `${proxyUrl}/v2/vehicle/search/?${queryString}`;
};

export const searchStockNumber = (
    stockNumber: string,
    orgId: string,
    bffEndpointConfig: IConfigObj
): ((dispatch: Dispatch<any, any>) => void) => {
    const endpoint = buildVehicleSearchV2Url({ stockNumber, commonOrgDealerId: orgId }, bffEndpointConfig);

    return (dispatch) => {
        dispatch({
            [RSAA]: {
                endpoint,
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                types: [
                    SEARCH_STOCK_NUMBER_REQUEST,
                    SEARCH_STOCK_NUMBER_SUCCESS,
                    {
                        type: SEARCH_STOCK_NUMBER_FAILURE,
                        meta: (_action: RSAACallAction, _state: any, res: Response) => {
                            const statusCode = res?.status;
                            return { isPermissionError: statusCode === 403 };
                        }
                    }
                ],
                fetch: proxyFetch
            } satisfies RSAACallRequest
        } as RSAACallAction);
    };
};

let aiSearchVinInventoryAbortController: AbortController;

/**
 * Asks live inventory whether a VIN prompt is still sellable here, since the AI index reports a sold
 * vehicle as `ACTIVE` with its real `stockNumber`. Queries by `vehicleIdentifier` (the field the
 * producer validates as a VIN), upper-cased because that endpoint does not normalise it, across every
 * rooftop the search covered. A second check aborts the first.
 */
export const verifyAiSearchVinInventory = (
    vin: string,
    commonOrgDealerIds: string[],
    bffEndpointConfig: IConfigObj
): ((dispatch: Dispatch<any, any>) => void) => {
    if (aiSearchVinInventoryAbortController) {
        aiSearchVinInventoryAbortController.abort();
    }
    aiSearchVinInventoryAbortController = new AbortController();

    const commonOrgDealerId = commonOrgDealerIds.join(',');

    const endpoint = buildVehicleSearchV2Url({ vehicleIdentifier: vin.trim().toUpperCase(), commonOrgDealerId }, bffEndpointConfig);

    return (dispatch) => {
        dispatch({
            [RSAA]: {
                endpoint,
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                options: {
                    signal: aiSearchVinInventoryAbortController.signal
                },
                types: [
                    // Fails open, so the initiated/failed pair per search is the only signal that it
                    // stopped working — one request covers every rooftop. Aborts reported apart from failures.
                    {
                        type: AI_SEARCH_VIN_INVENTORY_REQUEST,
                        meta: () => {
                            logNewRelicPageAction('AI search VIN inventory check initiated', { commonOrgDealerId });
                        }
                    },
                    {
                        type: AI_SEARCH_VIN_INVENTORY_SUCCESS,
                        // Static, not a callback: the reducer matches this against the current prompt
                        // before it trusts the answer.
                        meta: { vin }
                    },
                    {
                        type: AI_SEARCH_VIN_INVENTORY_FAILURE,
                        // (action, state, res) — the middleware passes the response third.
                        meta: (action: RSAACallAction, _state: unknown, res: Response) => {
                            const options = action[RSAA].options as RequestInit | undefined;
                            const isAborted = options?.signal?.aborted || false;

                            logNewRelicPageAction(`AI search VIN inventory check ${isAborted ? 'canceled' : 'failed'}`, {
                                success: false,
                                statusCode: res?.status,
                                isAborted
                            });

                            return { isAborted };
                        }
                    }
                ],
                fetch: proxyFetch
            } satisfies RSAACallRequest
        } as RSAACallAction);
    };
};

let abortController: AbortController;

export const searchVehiclesV2 = (
    queryParams: VehicleSearchV2QueryParams,
    bffEndpointConfig: IConfigObj
): ((dispatch: Dispatch<any, any>) => void) => {
    if (abortController) {
        abortController.abort();
    }
    abortController = new AbortController();

    const endpoint = buildVehicleSearchV2Url(queryParams, bffEndpointConfig);

    return (dispatch) => {
        dispatch({
            [RSAA]: {
                endpoint,
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                options: {
                    signal: abortController.signal
                },
                types: [
                    SEARCH_VEHICLE_REQUEST,
                    {
                        type: SEARCH_VEHICLE_SUCCESS,
                        meta: () => {
                            logNewRelicPageAction('SearchV2 - Search completed successfully', {
                                success: true
                            });
                        }
                    },
                    {
                        type: SEARCH_VEHICLE_FAILURE,
                        meta: (action: RSAACallAction, res: Response) => {
                            const options = action[RSAA].options as RequestInit | undefined;
                            const isAborted = options?.signal?.aborted || false;
                            const statusCode = res?.status;

                            logNewRelicPageAction(`SearchV2 - Search ${isAborted ? 'canceled' : 'failed'}`, {
                                success: false,
                                statusCode,
                                isAborted
                            });

                            return { isAborted };
                        }
                    }
                ],
                fetch: async (...args: Parameters<typeof fetch>) => {
                    return await fetch(...args);
                }
            } satisfies RSAACallRequest
        } as RSAACallAction);
        dispatch({ type: SET_SEARCH_QUERY, payload: { searchQuery: queryParams.search } });
    };
};

export const searchVehiclesWithFilters = (
    { appliedFilters, search, commonOrgDealerId, sort, page, pageSize }: VehicleSearchOptions,
    bffEndpointConfig: IConfigObj,
    loggingContext?: { defaultOrgId: string }
) => {
    const appliedFilterParams = appliedFilters ? mapAppliedFiltersToQueryParams(appliedFilters) : {};
    const searchVehiclesV2WithNewRelic = withNewRelic(searchVehiclesV2, 'SearchV2 - Search initiated', loggingContext);
    return searchVehiclesV2WithNewRelic(
        {
            sort,
            search,
            commonOrgDealerId,
            page: page?.toString(),
            pageSize: pageSize?.toString(),
            ...appliedFilterParams
        },
        bffEndpointConfig
    );
};

export const fetchCatalogYears = (bffEndpointConfig: IConfigObj): RSAACallAction => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, 'vehicle');
    const endpoint = `${proxyUrl}/catalog/years`;

    const request: RSAACallRequest = {
        endpoint,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        types: [FETCH_CATALOG_YEARS_REQUEST, FETCH_CATALOG_YEARS_SUCCESS, FETCH_CATALOG_YEARS_FAILURE],
        fetch: proxyFetch
    };
    return {
        [RSAA]: request
    } as RSAACallAction;
};

export const fetchCatalogMakes = (bffEndpointConfig: IConfigObj, year: string): RSAACallAction => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, 'vehicle');
    const endpoint = `${proxyUrl}/catalog/makes/${encodeURIComponent(year)}`;

    const request: RSAACallRequest = {
        endpoint,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        types: [FETCH_CATALOG_MAKES_REQUEST, FETCH_CATALOG_MAKES_SUCCESS, FETCH_CATALOG_MAKES_FAILURE],
        fetch: proxyFetch
    };
    return {
        [RSAA]: request
    } as RSAACallAction;
};

export const fetchCatalogModels = (bffEndpointConfig: IConfigObj, year: string, make: string): RSAACallAction => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, 'vehicle');
    const endpoint = `${proxyUrl}/catalog/models/${encodeURIComponent(year)}/${encodeURIComponent(make)}`;

    const request: RSAACallRequest = {
        endpoint,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        types: [FETCH_CATALOG_MODELS_REQUEST, FETCH_CATALOG_MODELS_SUCCESS, FETCH_CATALOG_MODELS_FAILURE],
        fetch: proxyFetch
    };
    return {
        [RSAA]: request
    } as RSAACallAction;
};

export const fetchCatalogTrims = (bffEndpointConfig: IConfigObj, year: string, make: string, model: string): RSAACallAction => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, 'vehicle');
    const endpoint = `${proxyUrl}/catalog/trims/${encodeURIComponent(year)}/${encodeURIComponent(make)}/${encodeURIComponent(
        model
    )}`;

    const request: RSAACallRequest = {
        endpoint,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        types: [FETCH_CATALOG_TRIMS_REQUEST, FETCH_CATALOG_TRIMS_SUCCESS, FETCH_CATALOG_TRIMS_FAILURE],
        fetch: proxyFetch
    };
    return {
        [RSAA]: request
    } as RSAACallAction;
};

export const fetchCatalogVehicles = (
    bffEndpointConfig: IConfigObj,
    year: string,
    make: string,
    model: string,
    trim: string
): RSAACallAction => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, 'vehicle');
    const yearEscaped = encodeURIComponent(year);
    const makeEscaped = encodeURIComponent(make);
    const modelEscaped = encodeURIComponent(model);
    const trimEscaped = encodeURIComponent(trim);

    const endpoint = `${proxyUrl}/catalog/vehicles/${yearEscaped}/${makeEscaped}/${modelEscaped}/${trimEscaped}`;

    const request: RSAACallRequest = {
        endpoint,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        types: [FETCH_CATALOG_VEHICLES_REQUEST, FETCH_CATALOG_VEHICLES_SUCCESS, FETCH_CATALOG_VEHICLES_FAILURE],
        fetch: proxyFetch
    };
    return {
        [RSAA]: request
    } as RSAACallAction;
};

export const clearCatalogVehicles = () => {
    return {
        type: CLEAR_CATALOG_VEHICLES
    };
};

export const newVehicleSelected = (vehicle: IVehicle) => {
    return {
        type: NEW_VEHICLE_SELECTED,
        payload: { vehicle }
    };
};

export const processCviVehicles = (vehicles: IVehicle[]) => {
    return {
        type: PROCESS_CVI_VEHICLES,
        payload: vehicles
    };
};

export const searchBtnClicked = () => {
    return {
        type: SEARCH_VEHICLES_CLICKED
    };
};

export const changeVehicleTabChanged = () => {
    return {
        type: CHANGE_VEHICLE_TAB_CHANGED
    };
};

export const changeVehicleClicked = (vehicle: IVehicle, carryOverDiscounts = false, retainedItems?: RetentionDecisions) => {
    return {
        type: CHANGE_VEHICLE_CLICKED,
        payload: { selectedVehicle: vehicle, carryOverDiscounts, retainedItems }
    };
};

export const addNewScenarioClicked = (vehicle: IVehicle) => {
    return { type: ADD_NEW_SCENARIO_WITH_VEHICLE, payload: { selectedVehicle: vehicle } };
};

export const vehicleChangeCancel = () => {
    return {
        type: CHANGE_VEHICLE_CANCEL
    };
};

export const resetOnAddNewScenario = () => {
    return {
        type: RESET_ON_ADD_NEW_SCENARIO
    };
};

export const updateCustomVehicle = (vehicle: ChangeVehicleCustomVehicle | undefined) => {
    return {
        type: UPDATE_CUSTOM_VEHICLE,
        payload: vehicle
    };
};

export const updateVehicleCustomData = (vin: string, data: VehicleCustomData) => {
    return {
        type: UPDATE_VEHICLE_CUSTOM_DATA,
        payload: {
            vin,
            vehicleCustomData: data
        }
    };
};

export const finishVehicleChange = () => {
    return {
        type: VEHICLE_CHANGE_FINISH
    };
};

export const vehicleChangeSuccess = () => {
    return {
        type: VEHICLE_CHANGE_SUCCESS
    };
};

export const setOpenIncentivesAfterChange = (value: boolean) => {
    return {
        type: SET_OPEN_INCENTIVES_AFTER_CHANGE,
        payload: value
    };
};

export const setApplyVehicleRequested = (value: boolean) => {
    return {
        type: SET_APPLY_VEHICLE_REQUESTED,
        payload: value
    };
};

export const setAddNewScenarioRequested = (value: boolean) => {
    return {
        type: SET_ADD_NEW_SCENARIO_REQUESTED,
        payload: value
    };
};

export const vehicleChangeFailure = () => {
    return {
        type: VEHICLE_CHANGE_FAILURE
    };
};

export const processSwitchVehicle = () => {
    return {
        type: PROCESS_SWITCH_VEHICLE
    };
};

export const setAgentPromptAttr = (promptString: string, promptMode: string) => {
    return {
        type: SET_AGENT_PROMPT_ATTR,
        payload: { promptString, promptMode }
    };
};

// Action to request vehicle agent results on initial navigation (starts with similarity_search prompt mode with only vehicle object)
export const processVehicleAgentSimilarityRequest = (vehicle?: VehicleObjectForSimilaritySearch, trigger?: AI_REQUEST_TRIGGER) => {
    return {
        type: PROCESS_VEHICLE_AGENT_REQUEST,
        payload: { vehicle, trigger }
    };
};

// Action to request vehicle agent results with applied filters, sorting, and pagination on user interaction
// Can include vehicle object if still in similarity_search mode (user does not change search prompt)
export const processVehicleAgentRequestWithFilters = (
    payload: {
        appliedFilters?: AppliedFilter[];
        sortBy?: string;
        selectedPage?: number;
        vehicle?: VehicleObjectForSimilaritySearch;
        trigger?: AI_REQUEST_TRIGGER;
    } = {}
) => {
    const { appliedFilters, sortBy, selectedPage, vehicle, trigger } = payload;
    return {
        type: PROCESS_VEHICLE_AGENT_REQUEST,
        payload: { appliedFilters, sortBy, selectedPage, vehicle, trigger }
    };
};

export const dismissInventoryFallback = () => ({
    type: DISMISS_INVENTORY_FALLBACK
});

// Thunk action to fetch vehicle agent filter summary data (all vehicles)
export const fetchVehicleAgentFilterSummary = (
    commonOrgDealerId: string,
    commonOrgDealerIds: string[] = [],
    bffEndpointConfig: IConfigObj,
    skipModelProcessing = false
): ((dispatch: Dispatch<any, any>) => void) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig);
    const endpoint = `${proxyUrl}/agent/vehicle`;

    return (dispatch) => {
        const coxAutoCorrelationId = generateUuid();
        const request: RSAACallRequest = {
            endpoint,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                [COX_AUTO_CORRELATION_ID_HEADER]: coxAutoCorrelationId
            },
            body: JSON.stringify({
                commonOrgDealerId,
                ...(commonOrgDealerIds.length && { commonOrgDealerIds }),
                promptMode: AI_PROMPT_MODE.SEMANTIC_SEARCH,
                ...(skipModelProcessing ? { includeVehicles: false } : { promptString: AI_PROMPT_PLACEHOLDER.ALL_VEHICLES })
            }),
            types: [
                VEHICLE_AGENT_FILTER_REQUEST,
                { type: VEHICLE_AGENT_FILTER_SUCCESS, meta: { coxAutoCorrelationId } },
                { type: VEHICLE_AGENT_FILTER_FAILURE, meta: { coxAutoCorrelationId } }
            ]
        };

        dispatch({
            [RSAA]: request
        } as RSAACallAction);
    };
};

let vehicleAgentAbortController: AbortController;

// Thunk action to fetch vehicle agent results (based on prompt string, filters, sorting, pagination, and optionally vehicle object)
export const fetchVehicleAgentResults = (
    promptString: string,
    commonOrgDealerId: string,
    bffEndpointConfig: IConfigObj,
    applicationName: string,
    commonOrgDealerIds?: string[],
    vehicleStockNumber?: string,
    vehicleVin?: string,
    promptMode?: string,
    filters?: FilterObjectForSemanticSearch,
    selectedPage?: number,
    vehicle?: VehicleObjectForSimilaritySearch,
    sortBy?: string,
    dealId?: string,
    isPromptOptimizationEnabled?: boolean,
    trigger?: AI_REQUEST_TRIGGER,
    bodyStyleRelaxationEnabled?: boolean
): ((dispatch: Dispatch<any, any>, getState: () => StateTree) => void) => {
    // Cancel previous request if it exists
    if (vehicleAgentAbortController) {
        vehicleAgentAbortController.abort();
    }
    vehicleAgentAbortController = new AbortController();

    const proxyUrl = buildFromConfig(bffEndpointConfig);
    const endpoint = `${proxyUrl}/agent/vehicle`;

    return (dispatch) => {
        const coxAutoCorrelationId = generateUuid();
        const isPlaceholderPrompt =
            promptMode === AI_PROMPT_MODE.SEMANTIC_SEARCH &&
            (promptString === AI_PROMPT_PLACEHOLDER.SEARCH || promptString === AI_PROMPT_PLACEHOLDER.ALL_VEHICLES);
        const skipModelProcessing = isPromptOptimizationEnabled && isPlaceholderPrompt;
        // Build the API payload based on the prompt mode
        const basePayload = {
            commonOrgDealerId,
            applicationName,
            ...(commonOrgDealerIds?.length && { commonOrgDealerIds }),
            ...(selectedPage && { page: selectedPage }),
            ...(promptMode && promptMode !== AI_PROMPT_MODE.DEFAULT && { promptMode }),
            ...(filters && Object.keys(filters).length > 0 && { filters }),
            ...(sortBy && { sort: sortBy }),
            ...(dealId && { dealId })
        };

        // For similarity_search mode, use vehicle object; for others, use promptString
        const payload =
            promptMode === AI_PROMPT_MODE.SIMILARITY_SEARCH && vehicle
                ? {
                      ...basePayload,
                      vehicle
                  }
                : {
                      ...basePayload,
                      ...(!skipModelProcessing && { promptString })
                  };

        const request: RSAACallRequest = {
            endpoint,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                [COX_AUTO_CORRELATION_ID_HEADER]: coxAutoCorrelationId
            },
            body: JSON.stringify(payload),
            options: {
                signal: vehicleAgentAbortController.signal
            },
            types: [
                VEHICLE_AGENT_REQUEST,
                {
                    type: VEHICLE_AGENT_SUCCESS,
                    meta: { vehicleVin, vehicleStockNumber, promptMode, coxAutoCorrelationId, trigger, bodyStyleRelaxationEnabled }
                },
                {
                    type: VEHICLE_AGENT_FAILURE,
                    meta: (action: RSAACallAction, _res: Response) => {
                        const options = action[RSAA].options as RequestInit | undefined;
                        const isAborted = options?.signal?.aborted || false;
                        return { isAborted, coxAutoCorrelationId, trigger };
                    }
                }
            ]
        };

        dispatch({
            [RSAA]: request
        } as RSAACallAction);
    };
};
